import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import {
  MotifFormField,
  MotifSelect,
  MotifOption,
  MotifBadge,
  MotifProgressLoader,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
  MotifToast,
  MotifInlineMessage,
  MotifTextLink,
  MotifIcon,
  MotifIconButton,
} from '@ey-xd/motif-react'
import { MotifButton } from '@ey-xd/motif-react'
import { useParams } from 'react-router-dom'
import Table from '@/components/Table/table'
import { customTableLoadingData } from '@/components/Table/CustomPaginationConstants'
import {
  AppropriatenessStatusType,
  useGetAppropriatenessStatusListQuery,
  useGetAppropriatenessJobStatusQuery,
  useListAppropriatenessQuery,
  useUpdateAppropriatenessMutation,
  useUpdateAppropriatenessFieldsMutation,
  useStartAppropriatenessPipelineMutation,
  useResetAppropriatenessMutation,
} from '@/services/application/appropriatenessReducer'
import './index.module.scss'
import { AppropriatenessFilterType, EvidenceStatusEnum, JobRecordStatusType } from '@/services/types'
import { useListEvidenceQuery } from '@/services/application/evidenceReducer'
import UserFeedbackButtons from '@/components/userFeedback/UserFeedbackButtons'
import { SubmitUserFeedbackRequest, UserFeedbackReferenceType, useSubmitUserFeedbackMutation } from '@/services/application/userFeedbackReducer'
import { useGetHRIndexingStatusQuery } from '@/services/hrUpload/hrUploadReducer'
import styles from './index.module.scss'
import { IconoirInfoCircle, IconoirUser, IconoirCopy, IconoirWarningTriangle } from '@ey-xd/motif-icon'

// Cap on how many unverified evidence rows are fetched/displayed in the
// reference modal — keeps the payload and grid small even on large engagements.
const UNVERIFIED_EVIDENCE_DISPLAY_LIMIT = 100

const CommentCellRenderer = (props: any) => {
  return (
    <MotifBadge
      className="custom-comment-style"
      value={props.data.comments.length}
      variant="default"
    />
  )
}

const AppropriatenessStatus = (props: any) => {
  const [updateAppropriateness] = useUpdateAppropriatenessMutation()
  const appropriatenessId = props.data.id
  const currentValue = props.data.status
  const { data: statusList } = useGetAppropriatenessStatusListQuery()
  const selectedStatusLabel = statusList?.find((status: AppropriatenessStatusType) => status.id === currentValue)?.label ?? ''

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value
    await updateAppropriateness({
      appropriatenessId: appropriatenessId,
      statusType: selectedValue,
    })
    if (props.refetch) {
      props.refetch()
    }
  }

  return (
    <select
      value={currentValue}
      onChange={handleChange}
      title={selectedStatusLabel}
      style={{
        width: '100%',
        minWidth: 130,
        height: 40,
        borderRadius: 4,
        border: '1px solid #ccc',
        padding: '0 28px 0 10px',
        backgroundColor: '#fff',
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      <option value="00000000-0000-0000-0000-000000000000" disabled></option>
      {statusList?.map((status: AppropriatenessStatusType) => (
        <option key={status.id} value={status.id}>
          {status.label}
        </option>
      ))}
    </select>
  )
}

export const useColumnDeveloperAppropriateListDefs = (
  refetch: () => void,
  evidenceType: number,
  isEditing: boolean,
  onNavigateToEvidence?: () => void,
) => {
  const editableCellClass = (baseClass: string) =>
    isEditing ? `${baseClass} table-edit-preview-cell` : baseClass

  return [
    {
      headerName: '#',
      valueGetter: (params: any) => params.node ? params.node.rowIndex + 1 : '',
      width: 60,
      maxWidth: 60,
      sortable: false,
      filter: false,
      suppressSizeToFit: true,
      cellClass: 'align-left',
    },
    {
      field: 'userName',
      type: 'developerNameReferenceType',
      headerName: 'Name',
      cellClass: editableCellClass('text-wrap cell-break-word'),
      minWidth: 210,
      cellRenderer: (params: any) => {
        const hasSystemAccount = params.data.serviceAccount && String(params.data.serviceAccount).trim() !== ''
        const hasUserName = params.data.userName && String(params.data.userName).trim() !== ''
        if (hasSystemAccount && !hasUserName) {
          return !isEditing ? (
            <MotifTextLink
              href="#"
              onClick={(e: any) => {
                e.preventDefault()
                onNavigateToEvidence?.()
              }}
            >
              <MotifIcon src={IconoirInfoCircle} fill="#D97706" stroke="#D97706" />
              <em style={{ color: '#D97706' }}>Please upload evidence</em>
            </MotifTextLink>
          ) : ""
        }
        if (hasSystemAccount && hasUserName) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: '100%', lineHeight: 1 }}>
              <span>{params.value}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>
                <MotifIcon src={IconoirUser} fill="#0917da" />
              </span>
            </div>
          )
        }
        return params.value
      }
    },
    {
      field: 'serviceAccount',
      type: 'serviceAccountReferenceType',
      headerName: 'System Account',
      cellClass: editableCellClass('text-wrap cell-break-word'),
    },
    {
      field: 'userId',
      type: 'developerIdReferenceType',
      headerName: 'ID',
      cellClass: editableCellClass('align-left'),
      width: 120,
    },
    {
      field: 'userEmail',
      type: 'userEmailReferenceType',
      headerName: 'Email',
      minWidth: 210,
      cellClass: styles.emailCell,
      tooltipField: 'userEmail',
    },
    {
      field: 'jobTitle',
      type: 'userEmailReferenceType',
      headerName: 'Job Title',
      cellClass: editableCellClass('text-wrap cell-break-word'),
      minWidth: 200,
    },
    {
      field: 'environment',
      type: 'environmentReferenceType',
      headerName: 'Env',
      cellClass: editableCellClass('text-wrap'),
      minWidth: 80,
    },
    {
      field: 'department',
      type: 'userGroupReferenceType',
      headerName: 'Department',
      minWidth: 210,
      cellClass: editableCellClass('text-wrap cell-break-word'),
    },
    {
      field: 'userPermission',
      type: 'userGroupReferenceType',
      headerName: 'Permission',
      cellClass: editableCellClass('text-wrap'),
    },
    {
      field: 'hrMatchMessage',
      headerName: 'Match Note',
      minWidth: 280,
      cellClass: editableCellClass('text-wrap cell-break-word'),
    },
    {
      comparator: () => 0,
      field: 'status',
      type: 'appropriateReferenceType',
      headerName: 'Appropriateness',
      cellClass: 'align-center',
      overflow: 'show',
      cellRenderer: (props: any) => (
        <AppropriatenessStatus {...props} refetch={refetch} />
      ),
      minWidth: 180
    },
  ]
}

type EvidenceType = {
  evidenceType: number
  onNavigateToEvidence?: () => void
}

const BLOCKED_MESSAGES: Record<string, string[]> = {
  no_evidence: [
    'No evidence files have been uploaded yet.',
    'Upload at least one developer or implementer evidence file and ensure it is annotated.',
  ],
  evidence_not_verified: [
    'Not all uploaded evidence files have been verified.',
    'Open each file in the annotation view and complete annotation until the status shows "Verified".',
  ],
  too_many_failures: [
    'The appropriateness pipeline has failed 3 or more times in the last 10 minutes.',
    'Please wait a few minutes before retrying, or contact support if the issue persists.',
  ],
}

export default function Appropriateness(props: EvidenceType): JSX.Element {
  const tableLoadingData = customTableLoadingData
  const evidenceType = props?.evidenceType || 1
  const onNavigateToEvidence = props?.onNavigateToEvidence
  const { applicationId, engagementId } = useParams<{
    applicationId: string
    engagementId: string
  }>()
  const [isEditing, setIsEditing] = useState(false)
  // pendingEdits: rowId -> {field: newValue }
  const [pendingEdits, setPendingEdits] = useState<Record<string, Record<string, string>>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [updateAppropriatenessFields] = useUpdateAppropriatenessFieldsMutation()
  const [startPipeline] = useStartAppropriatenessPipelineMutation()
  const [resetAppropriateness, { isLoading: isResetting }] = useResetAppropriatenessMutation()
  const [showRerunModal, setShowRerunModal] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  const [isRerunning, setIsRerunning] = useState(false)
  // The job id that existed (or null) right before the current rerun was
  // triggered -- used to detect a genuinely NEW job below, rather than
  // relying on ever observing isInProgress: true, which a job that runs and
  // completes faster than the poll interval would never be caught doing.
  const rerunBaselineJobIdRef = useRef<string | null>(null)
  const pendingEditsRef = useRef<Record<string, Record<string, string>>>({})
  const tableGridRef = useRef<any>(null)
  const editableTextFields = useMemo(
    () => new Set([
      'userName',
      'serviceAccount',
      'userId',
      'userEmail',
      'jobTitle',
      'environment',
      'department',
      'userPermission',
    ]),
    [],
  )

  const [filterType] = useState<
    {
      value: string
      label: string
      isSelected?: boolean
    }[]
  >([
    {
      value: AppropriatenessFilterType.Department,
      label: 'Department',
      isSelected: false,
    },
    {
      value: AppropriatenessFilterType.AppropriatenessYes,
      label: 'Appropriateness Yes',
      isSelected: false,
    },
    {
      value: AppropriatenessFilterType.AppropriatenessNo,
      label: 'Appropriateness No',
      isSelected: false,
    },
    {
      value: AppropriatenessFilterType.AppropriatenessNeedMoreInfo,
      label: 'Appropriateness Need more information',
      isSelected: false,
    },
  ])
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const {
    data: appropriatenessList,
    error,
    isLoading: appropriatenessLoading,
    refetch: listAppropriate,
  } = useListAppropriatenessQuery(
    {
      applicationId: applicationId || '',
      type: evidenceType,
      filter: selectedOptions,
    },
    // Always hit the API on mount — prevents stale RTK Query cache when user navigates back.
    { refetchOnMountOrArgChange: true }
  )

  // Poll job status so we always know when a pipeline run completes, even when
  // the user navigates back to the page mid-run or right after a verification
  // kicks off a new job. Always poll slowly while idle so we catch newly-started
  // jobs; speed up while one is actively running.
  const [jobPollingInterval, setJobPollingInterval] = useState(15000)
  const { data: jobStatus, isLoading: jobStatusLoading, refetch: refetchJobStatus } = useGetAppropriatenessJobStatusQuery(
    { applicationId: applicationId || '', type: evidenceType },
    { pollingInterval: jobPollingInterval, refetchOnMountOrArgChange: true }
  )

  // Engagement-level HR indexing status — no cache so every mount/poll is fresh.
  // Poll every 10s while indexing; stop once complete.
  const [hrPollingInterval, setHrPollingInterval] = useState(10000)
  const { data: hrIndexingData } = useGetHRIndexingStatusQuery(
    engagementId || '',
    { pollingInterval: hrPollingInterval, refetchOnMountOrArgChange: true }
  )
  useEffect(() => {
    if (hrIndexingData?.isIndexing === false) setHrPollingInterval(0)
    else if (hrIndexingData?.isIndexing === true) setHrPollingInterval(10000)
  }, [hrIndexingData?.isIndexing])
  // New endpoint is the source of truth; fall back to jobStatus only before first fetch completes
  const isHRIndexing = hrIndexingData ? hrIndexingData.isIndexing : (jobStatus?.isHRIndexing ?? false)

  // Unverified-evidence readiness check — independent of job/table phase so it
  // still shows even when the page displays a previously-completed (stale)
  // result set. Uses summary=true so the BE skips per-item annotation/system
  // account enrichment (not needed here). Polls only while items are unverified.
  const [evidencePollingInterval, setEvidencePollingInterval] = useState(10000)
  const { data: evidenceSummaryList } = useListEvidenceQuery(
    { applicationId: applicationId || '', evidenceType, summary: true },
    { skip: !applicationId, pollingInterval: evidencePollingInterval, refetchOnMountOrArgChange: true }
  )
  const unverifiedEvidence = useMemo(
    () =>
      Array.isArray(evidenceSummaryList)
        // Only flag evidence that's actually annotated but not yet verified —
        // freshly-uploaded/scanned evidence with no annotations yet has
        // nothing to verify, so don't show it as "unverified".
        ? evidenceSummaryList.filter((e: any) => e.evidenceStatus !== EvidenceStatusEnum.Verified && e.hasAnnotations)
        : [],
    [evidenceSummaryList]
  )
  const hasUnverifiedEvidence = unverifiedEvidence.length > 0
  useEffect(() => {
    setEvidencePollingInterval(hasUnverifiedEvidence ? 10000 : 0)
  }, [hasUnverifiedEvidence])
  const [showUnverifiedModal, setShowUnverifiedModal] = useState(false)
  const [copyLabel, setCopyLabel] = useState('Copy table')
  const unverifiedEvidenceRows = useMemo(
    () =>
      unverifiedEvidence.slice(0, UNVERIFIED_EVIDENCE_DISPLAY_LIMIT).map((e: any) => ({
        fileName: e.fileName,
        // evidenceStatusName is the raw C# enum name (e.g. "Yet_To_Be_Verified") —
        // humanize it instead of showing it verbatim.
        status: (e.evidenceStatusName ?? String(e.evidenceStatus)).replace(/_/g, ' '),
        env: e.environment ?? '',
      })),
    [unverifiedEvidence]
  )
  const unverifiedEvidenceColumnDefs = useMemo(
    () => [
      { headerName: 'File Name', field: 'fileName', flex: 2 },
      { headerName: 'Status', field: 'status', flex: 1 },
      { headerName: 'Env', field: 'env', flex: 1 },
    ],
    []
  )
  const copyLabelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    return () => {
      if (copyLabelTimeoutRef.current) clearTimeout(copyLabelTimeoutRef.current)
    }
  }, [])
  const handleCopyUnverifiedTable = useCallback(async () => {
    // Copy ALL unverified evidence, not just the display-capped
    // (UNVERIFIED_EVIDENCE_DISPLAY_LIMIT) rows shown in the modal table.
    const allRows = unverifiedEvidence.map((e: any) => ({
      fileName: e.fileName,
      status: (e.evidenceStatusName ?? String(e.evidenceStatus)).replace(/_/g, ' '),
      env: e.environment ?? '',
    }))
    const rows = [['File Name', 'Status', 'Env'], ...allRows.map((r) => [r.fileName, r.status, r.env])]
    const tsv = rows.map((r) => r.join('\t')).join('\n')
    try {
      await navigator.clipboard.writeText(tsv)
      setCopyLabel('Copied!')
    } catch {
      setCopyLabel('Copy failed')
    } finally {
      if (copyLabelTimeoutRef.current) clearTimeout(copyLabelTimeoutRef.current)
      copyLabelTimeoutRef.current = setTimeout(() => setCopyLabel('Copy table'), 2000)
    }
  }, [unverifiedEvidence])

  // ── Single-source phase (priority order — first match wins) ──
  // A genuinely active job (local optimistic isRerunning flag, or the polled
  // jobStatus) must always win over stale/leftover hasData AND over
  // isHRIndexing — otherwise re-running (or navigating back to this step
  // while a job is running elsewhere/after a remount resets isRerunning)
  // silently shows old results with no indication a job is active, and
  // polling updates never surface to the user. The backend applies its own
  // staleness recovery to stuck/never-picked-up jobs, so this can't mask
  // real completed results indefinitely.
  //
  // 'running' vs 'processing': the UI only claims a job is "truly running"
  // once it's actually executing in the Azure Function (JobStatusId ===
  // InProgress). A job that's merely queued (NotStarted — created
  // synchronously but not yet dequeued) or already handed off to the async
  // AI/LLM evaluation step (Submitted — waiting on a job-results-notify/
  // job-check-in callback) is active but NOT running, so it gets its own
  // "a request is being processed" treatment instead of the "matching
  // against HR data" running copy.
  const hasData = Array.isArray(appropriatenessList) && appropriatenessList.length > 0
  const jobStatusId = jobStatus?.jobStatusId
  const isTrulyRunning = jobStatusId === JobRecordStatusType.InProgress
  const isProcessing = jobStatusId === JobRecordStatusType.NotStarted || jobStatusId === JobRecordStatusType.Submitted
  type Phase = 'loading' | 'running' | 'processing' | 'hr-indexing' | 'failed' | 'blocked' | 'ready'
  const phase = useMemo<Phase>(() => {
    if (isTrulyRunning) return 'running'
    if (isRerunning) return 'processing'
    if (isProcessing) return 'processing'
    if (isHRIndexing) return 'hr-indexing'
    if (appropriatenessLoading || jobStatusLoading) return 'loading'
    // Fallback: isInProgress true but jobStatusId absent/unexpected — treat
    // conservatively as "processing" rather than assume active AZF execution.
    if (jobStatus?.isInProgress) return 'processing'
    if (hasData) return 'ready'
    if (jobStatus?.isStale) return 'failed'
    if (jobStatusId === JobRecordStatusType.Failed) return 'failed'
    if (jobStatus?.blockedReason) return 'blocked'
    return 'ready'
  }, [isTrulyRunning, isRerunning, isProcessing, isHRIndexing, appropriatenessLoading, jobStatusLoading, hasData, jobStatus, jobStatusId])

  // Adjust polling rate based on job state.
  useEffect(() => {
    setJobPollingInterval(phase === 'running' || phase === 'processing' ? 8000 : 15000)
    // Clear the optimistic isRerunning flag once polling observes a DIFFERENT
    // job than whatever existed right before this rerun was triggered -- not
    // just "isInProgress became true" -- so a job that runs and completes
    // faster than the poll interval (and is therefore never caught mid-flight)
    // still clears isRerunning instead of getting stuck on 'processing' forever.
    if (isRerunning && jobStatus?.jobId && jobStatus.jobId !== rerunBaselineJobIdRef.current) {
      setIsRerunning(false)
    }
  }, [phase, isRerunning, jobStatus?.jobId])

  // When job transitions in-progress → done, reload the list.
  const prevJobInProgress = useRef<boolean | undefined>(undefined)
  useEffect(() => {
    if (prevJobInProgress.current === true && jobStatus?.isInProgress === false) {
      listAppropriate()
    }
    prevJobInProgress.current = jobStatus?.isInProgress
  }, [jobStatus?.isInProgress, listAppropriate])

  // ── Manual trigger only ──
  // Appropriateness no longer auto-starts silently. Instead a "Run
  // Appropriateness" button is shown once evidence is verified and no
  // results/job exist yet (see phase === 'ready' && tableData.length === 0
  // block in the render below) so the user always has explicit, visible
  // control over when the pipeline kicks off.

  const [submitFeedback] = useSubmitUserFeedbackMutation()

  const handleFilterChange = (event: string[]) => {
    const selectedOptions = event
    const selectedOptionsArray = Array.from(selectedOptions)
    setSelectedOptions(selectedOptionsArray)
  }

  const columnDefs = useMemo(() =>
    useColumnDeveloperAppropriateListDefs(listAppropriate, evidenceType, isEditing, onNavigateToEvidence).map((colDef: any) => {
      const fieldName = colDef?.field
      if (!fieldName || !editableTextFields.has(fieldName)) {
        return colDef
      }

      return {
        ...colDef,
        cellClass: (params: any) => {
          const baseClass = typeof colDef.cellClass === 'string' ? colDef.cellClass : ''
          const rowId = params?.data?.id
          const hasPendingChange = !!(rowId && pendingEdits[rowId]?.[fieldName] !== undefined)
          const editPreviewClass = isEditing ? ' table-edit-preview-cell' : ''
          const changedClass = hasPendingChange ? ' table-cell-changed' : ''
          return `${baseClass}${editPreviewClass}${changedClass}`.trim()
        },
      }
    }),
    [listAppropriate, evidenceType, isEditing, onNavigateToEvidence, editableTextFields, pendingEdits]
  )

  const defaultColDef = useMemo(() => ({
    editable: (params: any) => {
      const fieldName = params?.colDef?.field
      return isEditing && typeof fieldName === 'string' && editableTextFields.has(fieldName)
    },
  }), [isEditing, editableTextFields])

  // AG Grid edits row objects in-place. Clone RTK query results so grid does not mutate frozen cache objects.
  // Only clear the table during an explicit user-triggered rerun (isRerunning)
  // when there's no data yet. If the list already has data, show it regardless
  // of job status — the BE can report isInProgress for a NotStarted (status 0)
  // job that was never actually run.
  const tableData = useMemo(() => {
    if (isRerunning && !hasData) return []
    return Array.isArray(appropriatenessList)
      ? appropriatenessList.map((row) => ({ ...row }))
      : []
  }, [appropriatenessList, isRerunning, hasData])

  const unmatchedRows = useMemo(
    () => {
      if (isRerunning && !hasData) return []
      return Array.isArray(appropriatenessList)
        ? appropriatenessList.filter((row) => typeof row.hrMatchMessage === 'string' && row.hrMatchMessage.trim() !== '')
        : []
    },
    [appropriatenessList, isRerunning, hasData]
  )

  const handleFeedbackSubmit = useCallback((feedback: SubmitUserFeedbackRequest) => {
    if (applicationId) {
      feedback.referenceId = applicationId;
      submitFeedback(feedback)
        .unwrap()
        .then((response) => {
          console.log('Feedback submitted successfully:', response);
        })
        .catch((error) => {
          console.error('Error submitting feedback:', error);
        });
    }
  }, [applicationId, submitFeedback])

  const handleCellValueChanged = useCallback((params: any) => {
    const { data, colDef, newValue, oldValue } = params
    if (!colDef.field || !data?.id) return
    if (!isEditing) return
    if (newValue === oldValue) return

    const next = {
      ...pendingEditsRef.current,
      [data.id]: {
        ...(pendingEditsRef.current[data.id] ?? {}),
        [colDef.field]: newValue,
      },
    }
    pendingEditsRef.current = next
    setPendingEdits((prev) => ({
      ...prev,
      [data.id]: {
        ...(prev[data.id] ?? {}),
        [colDef.field]: newValue,
      },
    }))
  }, [isEditing])

  const handleSaveEdits = useCallback(async () => {
    // Force-commit active cell editor values before building payload.
    tableGridRef.current?.api?.stopEditing?.()
    await new Promise((resolve) => setTimeout(resolve, 0))

    const editsToSave = pendingEditsRef.current
    if (Object.keys(editsToSave).length === 0) {
      setIsEditing(false)
      return
    }
    setIsSaving(true)
    try {
      await Promise.all(
        Object.entries(editsToSave).map(([appropriatenessId, fields]) =>
          updateAppropriatenessFields({ appropriatenessId, ...fields }).unwrap()
        )
      )
      setPendingEdits({})
      pendingEditsRef.current = {}
      setIsEditing(false)
      listAppropriate()
    } finally {
      setIsSaving(false)
    }
  }, [updateAppropriatenessFields, listAppropriate])

  const handleToggleEdit = useCallback(() => {
    if (isEditing) {
      handleSaveEdits()
    } else {
      setIsEditing(true)
    }
  }, [isEditing, handleSaveEdits])

  const handleCancelEdits = useCallback(() => {
    tableGridRef.current?.api?.stopEditing?.(true)
    setPendingEdits({})
    pendingEditsRef.current = {}
    setIsEditing(false)

    const resetRows = Array.isArray(appropriatenessList)
      ? appropriatenessList.map((row) => ({ ...row }))
      : []

    // Reset grid view back to server data snapshot.
    if (typeof tableGridRef.current?.api?.setRowData === 'function') {
      tableGridRef.current.api.setRowData(resetRows)
    } else if (typeof tableGridRef.current?.api?.setGridOption === 'function') {
      tableGridRef.current.api.setGridOption('rowData', resetRows)
    }
  }, [appropriatenessList])

  const handleForceRerun = useCallback(async () => {
    if (!applicationId) return
    try {
      rerunBaselineJobIdRef.current = jobStatus?.jobId ?? null
      setIsRerunning(true)
      await startPipeline({ applicationId, type: evidenceType }).unwrap()
      setStartError(null)
      refetchJobStatus()
    } catch (err: any) {
      // Surface the BE's actual failure reason (e.g. "evidence not verified",
      // user-lookup failure) instead of a generic message -- a bare `catch {}`
      // here previously swallowed the real cause entirely, making a failed
      // submit indistinguishable from "never run" once the user navigated
      // away and this local state was reset.
      console.error('Failed to start appropriateness pipeline', err)
      setIsRerunning(false)
      setStartError(err?.data?.message || 'Failed to start the appropriateness analysis. Please try again.')
    }
  }, [applicationId, evidenceType, startPipeline, refetchJobStatus, jobStatus?.jobId])

  const handleRerunConfirm = useCallback(async () => {
    if (!applicationId) return
    setShowRerunModal(false)
    try {
      rerunBaselineJobIdRef.current = jobStatus?.jobId ?? null
      setIsRerunning(true)
      // DeleteAppropriatenessRecords (the /reset endpoint) only wipes the old
      // Appropriateness + JobRecord rows -- it never enqueues a new job. Without
      // this explicit startPipeline call, "Force re-run" silently deletes
      // existing results and leaves nothing running: jobStatus correctly
      // reports jobStatusId: null (no job exists) forever, since nothing ever
      // re-queues one.
      await resetAppropriateness({ applicationId, type: evidenceType }).unwrap()
      await startPipeline({ applicationId, type: evidenceType }).unwrap()
      setStartError(null)
      listAppropriate()
      refetchJobStatus()
    } catch (err: any) {
      console.error('Failed to reset/rerun appropriateness', err)
      setIsRerunning(false)
      setStartError(err?.data?.message || 'Failed to reset appropriateness. Please try again.')
    }
  }, [applicationId, evidenceType, resetAppropriateness, startPipeline, listAppropriate, refetchJobStatus, jobStatus?.jobId])

  const listLabel = evidenceType === 1 ? 'Developer' : 'Implementor'

  //style row based on the condition
  const getRowStyle = useCallback((params: any) => {
    if (!params.data) return {}

    const hasUserName = params.data.userName && String(params.data.userName).trim() !== ''
    const hasServiceAccount = params.data.serviceAccount && String(params.data.serviceAccount).trim() !== ''

    // Both exist: light blue
    if (hasUserName && hasServiceAccount) {
      return { background: "#EEF9FF", border: "1px solid #B3E5FC" }
    }

    // Service account exists but name is missing: orange
    if (hasServiceAccount && !hasUserName) {
      return { background: "#FFF9E6", border: "1px solid #FFD580" }
    }

    return {}
  }, [])

  const systemAccountCount = tableData.filter(row => row.serviceAccount && String(row.serviceAccount).trim() !== '').length

  return (
    <>
      {/* Start error banner */}
      {startError && (
        <div className={styles.staleBanner}>
          <span>{startError}</span>
          <MotifButton size="small" variant="primary" onClick={handleForceRerun} loading={isResetting} type="button">
            Retry
          </MotifButton>
        </div>
      )}

      {/* Stale / failed banner */}
      {phase === 'failed' && !startError && (
        <div className={styles.staleBanner}>
          <span>
            {jobStatus?.isStale
              ? 'The appropriateness job appears to be stuck (no activity for 10+ minutes).'
              : 'The last appropriateness job failed.'}
            {' '}You can force a re-run.
          </span>
          <MotifButton size="small" variant="primary" onClick={handleForceRerun} loading={isResetting} type="button">
            Force re-run
          </MotifButton>
        </div>
      )}

      {/* Blocked: explain missing preconditions. Skip when the block reason is
          unverified evidence — the banner below already covers that case with
          a "View list" action, so showing both would be a duplicate message. */}
      {phase === 'blocked' && jobStatus?.blockedReason !== 'evidence_not_verified' && (
        <div className={styles.staleBanner}>
          <span>
            {(BLOCKED_MESSAGES[jobStatus!.blockedReason!] ?? []).join(' ')}
          </span>
        </div>
      )}

      {/* HR indexing in progress */}
      {isHRIndexing && (
        <MotifToast variant="info" style={{ backgroundColor: '#e8f4fd', margin: '0.5rem 0' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span className={styles.spinner} />
            HR indexing is running. If you plan to re-run appropriateness, please wait until it&apos;s completed.
          </span>
        </MotifToast>
      )}

      {/* Unverified evidence — shown regardless of table phase so it's still
          visible even when a previous (stale) result set is on screen.
          Boxed banner style (matches the other warning banners on this page,
          and the equivalent unverified-evidence banner on the Input Files
          page) instead of a plain inline message so it reads as a blocking
          warning rather than incidental text. */}
      {hasUnverifiedEvidence && (
        <div className={styles.staleBanner} role="alert">
          <MotifIcon src={IconoirWarningTriangle} size="16" fill="#D07200" stroke="#D07200" aria-hidden="true" />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {unverifiedEvidence.length} {listLabel.toLowerCase()} evidence file{unverifiedEvidence.length !== 1 ? 's' : ''} still need{unverifiedEvidence.length === 1 ? 's' : ''} verification.
            What&apos;s shown below may not reflect the latest evidence.
            <MotifButton variant="text" size="small" onClick={() => setShowUnverifiedModal(true)} type="button">
              View list
            </MotifButton>
          </span>
        </div>
      )}

      <div className={`${styles.tableWrapper} ${isEditing ? styles.tableEditing : ''}`}>
        {/* In-progress overlay — truly running in the Azure Function */}
        {phase === 'running' && (
          <div className={styles.spinnerOverlay}>
            <div style={{ position: 'relative', width: 60, height: 60 }}>
              <MotifProgressLoader show />
            </div>
            <span className={styles.spinnerLabel}>
              Appropriateness is now running. You may navigate away and come back later.
            </span>
          </div>
        )}
        {phase === 'running' && (
          <div className={styles.matchSummaryBanner} role="status" style={{ backgroundColor: '#e8f4fd', borderColor: '#2196f3', color: '#0d47a1' }}>
            Matching users against HR data — this may take a moment. The list will refresh automatically when complete.
          </div>
        )}
        {/* Processing overlay — job is active (queued or submitted for async
            AI/LLM evaluation) but not yet actually executing in the Azure
            Function, so this uses distinct, less specific copy than 'running'. */}
        {phase === 'processing' && (
          <div className={styles.spinnerOverlay}>
            <div style={{ position: 'relative', width: 60, height: 60 }}>
              <MotifProgressLoader show />
            </div>
            <span className={styles.spinnerLabel}>
              A request is being processed. You may navigate away and come back later.
            </span>
          </div>
        )}
        {phase === 'processing' && (
          <div className={styles.matchSummaryBanner} role="status" style={{ position: 'relative', zIndex: 10000, backgroundColor: '#e8f4fd', borderColor: '#2196f3', color: '#0d47a1' }}>
            A request is being processed — this may take a moment. The list will refresh automatically when complete.
          </div>
        )}
        {phase === 'ready' && tableData.length > 0 && (
          <div className={styles.matchSummaryBanner} role="status">
            {tableData.length - unmatchedRows.length} user{tableData.length - unmatchedRows.length !== 1 ? 's' : ''} matched from HR list out of {tableData.length} total annotated.
            {unmatchedRows.length > 0 && ` ${unmatchedRows.length} unmatched ${systemAccountCount ? `and ${systemAccountCount} system ${systemAccountCount > 1 ? 'accounts' : 'account'} found` : ''} — see Match Note column.`}
          </div>
        )}
        <Table
          columnDefs={columnDefs}
          cellRenderers={tableData}
          defaultColDef={defaultColDef}
          onCellValueChanged={handleCellValueChanged}
          gridRef={tableGridRef}
          singleClickEdit={isEditing}
          suppressClickEdit={!isEditing}
          stopEditingWhenCellsLoseFocus={true}
          enableCellTextSelection={true}
          suppressMovableColumns={true}
          TitleComplement={() => (
            <div className={styles.tableheadergroup}>
              <div className={styles.tableHeaderRowTop}>
                {appropriatenessList && appropriatenessList.length > 0 && (
                  <UserFeedbackButtons referenceType={UserFeedbackReferenceType.Appropriateness} onFeedbackSubmit={handleFeedbackSubmit} />
                )}
                <div className={styles.tableActionGroup}>
                  {/* phase can only be 'ready' when HR indexing is NOT in
                      progress (see phase priority order above), so this
                      button is already absent — not merely disabled — while
                      HR indexing is running. */}
                  {tableData.length === 0 && phase === 'ready' && !isEditing && (
                    <MotifButton
                      size="small"
                      variant="primary"
                      onClick={handleForceRerun}
                      loading={isRerunning}
                      disabled={hasUnverifiedEvidence}
                      title={hasUnverifiedEvidence ? 'Cannot run until all evidence has been verified' : undefined}
                      type="button"
                    >
                      Run Appropriateness
                    </MotifButton>
                  )}
                  {/* Hidden (not just disabled) while HR indexing is in
                      progress — no button to trigger appropriateness should be
                      available until indexing completes. */}
                  {appropriatenessList && appropriatenessList.length > 0 && !isEditing && !isHRIndexing && (
                    <MotifButton
                      size="small"
                      variant="secondary"
                      onClick={() => setShowRerunModal(true)}
                      loading={isResetting}
                      disabled={hasUnverifiedEvidence}
                      title={hasUnverifiedEvidence ? 'Cannot rerun until all evidence has been verified' : undefined}
                      type="button"
                    >
                      Re-run
                    </MotifButton>
                  )}
                  {(appropriatenessList && appropriatenessList.length > 0) && isEditing && (
                    <MotifButton
                      size="small"
                      variant="secondary"
                      onClick={handleCancelEdits}
                      disabled={isSaving}
                      type="button"
                    >
                      Cancel
                    </MotifButton>
                  )}
                  {/* Entry point into editing ("Edit ... List") is hidden
                      while HR indexing is in progress, matching Run/Re-run.
                      An already-in-progress edit session stays visible
                      (Save) so the user isn't stranded mid-edit if indexing
                      starts while they're editing. */}
                  {appropriatenessList && appropriatenessList.length > 0 && (isEditing || !isHRIndexing) && (
                    <MotifButton
                      size="small"
                      variant={isEditing ? 'primary' : 'secondary'}
                      onClick={handleToggleEdit}
                      loading={isSaving}
                      disabled={isEditing && Object.keys(pendingEdits).length === 0}
                      type="button"
                    >
                      {isEditing ? 'Save' : `Edit ${listLabel} List`}
                    </MotifButton>
                  )}
                </div>
              </div>
              <div className={styles.tableHeaderRowBottom}>
                <MotifFormField>
                  <MotifSelect
                    usePortal={true}
                    ariaLabelledBy="select-label"
                    onChange={(e) => handleFilterChange(e)}
                    placeholder="Filter by"
                    title="Filter"
                    style={{ width: 295 }}
                    visibleOptions={4}
                    multiple={true}
                    value={selectedOptions}
                  >
                    {filterType.map((option) => (
                      <MotifOption
                        key={option.value}
                        value={option.value}
                        isSelected={option.isSelected}
                      >
                        {option.label}
                      </MotifOption>
                    ))}
                  </MotifSelect>
                </MotifFormField>
              </div>
            </div>
          )}
          title={evidenceType === 1 ? 'Developer List' : 'Implementor List'}
          description={
            'This test procedure will involve mapping all relevant users within the tool against HR data to determine their corresponding job titles and departments. This will enable the engagement team to evaluate the appropriateness of the users. Engagement teams are responsible to go line by line and assess the appropriateness of each user by either selecting “Yes”, “No” or “Needs more Information”. This test step can be skipped if evaluation of appropriateness is being covered in another control. '
          }
          pagination={true}
          overlayNoRowsTemplate={
            phase === 'loading' || phase === 'running' || phase === 'processing'
              ? '<span></span>'
              : phase === 'hr-indexing'
                ? 'Awaiting HR index completion'
                : error
                  ? 'Something went wrong!'
                  : 'No results yet. Verify all evidence, then run Appropriateness to populate this list.'
          }
          getRowStyle={getRowStyle}
        />
      </div>

      {/* Rerun confirmation modal */}
      <MotifModal show={showRerunModal} onClose={() => setShowRerunModal(false)} size="sm">
        <MotifModalHeader>Rerun Appropriateness?</MotifModalHeader>
        <MotifModalBody>
          This will delete existing records and rerun appropriateness. Please confirm.
        </MotifModalBody>
        <MotifModalFooter>
          <MotifButton variant="secondary" size="small" onClick={() => setShowRerunModal(false)} type="button">
            Cancel
          </MotifButton>
          <MotifButton variant="primary" size="small" onClick={handleRerunConfirm} loading={isResetting} type="button">
            Confirm
          </MotifButton>
        </MotifModalFooter>
      </MotifModal>

      {/* Unverified evidence reference modal */}
      <MotifModal show={showUnverifiedModal} onClose={() => setShowUnverifiedModal(false)} size="lg">
        <MotifModalHeader>Unverified {listLabel} Evidence ({unverifiedEvidence.length})</MotifModalHeader>
        <MotifModalBody>
          {unverifiedEvidence.length > UNVERIFIED_EVIDENCE_DISPLAY_LIMIT && (
            <MotifInlineMessage variant="info" className={styles.unverifiedModalInfo}>
              Showing the first {UNVERIFIED_EVIDENCE_DISPLAY_LIMIT} of {unverifiedEvidence.length} unverified files.
            </MotifInlineMessage>
          )}
          <div className={styles.unverifiedEvidenceGrid}>
            <Table
              columnDefs={unverifiedEvidenceColumnDefs}
              cellRenderers={unverifiedEvidenceRows}
              TitleComplement={() => null}
              title=""
              description=""
              pagination={false}
              rowHeight={30}
            />
          </div>
        </MotifModalBody>
        <MotifModalFooter>
          <MotifIconButton onClick={handleCopyUnverifiedTable} title={copyLabel} aria-label={copyLabel} type="button">
            <MotifIcon src={IconoirCopy} />
          </MotifIconButton>
          <span className={styles.copyLabel}>{copyLabel}</span>
          <MotifButton variant="secondary" size="small" onClick={() => setShowUnverifiedModal(false)} type="button">
            Close
          </MotifButton>
          <MotifButton
            variant="primary"
            size="small"
            onClick={() => {
              setShowUnverifiedModal(false)
              onNavigateToEvidence?.()
            }}
            type="button"
          >
            Go to Input Files
          </MotifButton>
        </MotifModalFooter>
      </MotifModal>
    </>
  )
}